import React from "react";
import {
  HiArrowLeft,
  HiArrowSmDown,
  HiArrowSmUp,
  HiCog,
  HiExclamationCircle,
  HiRefresh,
} from "react-icons/hi";
import {
  accuracyDelta,
  formatPercent,
  formatRelativeTime,
} from "./shared";
import { UseAgentTestLab } from "./useAgentTestLab";

interface ContextBarProps {
  lab: UseAgentTestLab;
  onBack: () => void;
}

export const AgentTestLabHeader: React.FC<ContextBarProps> = ({
  lab,
  onBack,
}) => {
  const latestAccuracy = lab.latestRun?.accuracy_score;
  const delta = accuracyDelta(lab.previousAccuracy, latestAccuracy);
  const lastRunAt = lab.latestRun?.started_at ?? lab.latestRun?.completed_at;

  return (
    <div className="sticky top-0 z-30 bg-[var(--card-container)]/95 backdrop-blur border-b border-[var(--border)]">
      <div className="container mx-auto px-4 py-3 flex flex-wrap items-center gap-x-5 gap-y-2">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to agent"
          className="text-[var(--foreground-muted)] hover:text-[var(--foreground)] inline-flex items-center gap-1.5 text-sm"
        >
          <HiArrowLeft className="w-4 h-4" />
          Back
        </button>

        <div className="h-5 w-px bg-[var(--border)]" />

        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-[var(--foreground)] truncate">
            {lab.agent?.name ?? "Agent"}
          </div>
          <div className="text-xs text-[var(--foreground-muted)] truncate">
            Test Lab · {lab.scenarios.length}{" "}
            {lab.scenarios.length === 1 ? "scenario" : "scenarios"}
            {lastRunAt && (
              <>
                {" "}
                · last run {formatRelativeTime(lastRunAt)}
              </>
            )}
          </div>
        </div>

        <Stat
          label="Accuracy"
          value={formatPercent(latestAccuracy)}
          accent={
            delta.direction === "up"
              ? "up"
              : delta.direction === "down"
                ? "down"
                : "neutral"
          }
          deltaLabel={
            delta.direction === "up" || delta.direction === "down"
              ? `${delta.direction === "up" ? "+" : ""}${formatPercent(delta.value ?? 0)}`
              : undefined
          }
        />
        <button
          type="button"
          onClick={() => {
            lab.setScenarioFilter(
              lab.scenarioFilter === "failed" ? "all" : "failed"
            );
            lab.setActiveTab("scenarios");
          }}
          disabled={lab.failedScenarios.length === 0}
          className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs transition-colors disabled:cursor-default ${
            lab.failedScenarios.length > 0
              ? "text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10"
              : "text-[var(--foreground-muted)]"
          }`}
          title={
            lab.failedScenarios.length > 0
              ? "Filter list to failed scenarios"
              : "No failures"
          }
        >
          <HiExclamationCircle className="w-4 h-4" />
          <span className="font-semibold tabular-nums">
            {lab.failedScenarios.length}
          </span>
          <span className="hidden md:inline">failing</span>
        </button>

        <div className="h-5 w-px bg-[var(--border)]" />

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={lab.resetProvidersToDefaults}
            className="px-3 py-1.5 text-xs font-medium bg-[var(--muted)] text-[var(--foreground)] rounded-md hover:bg-[var(--secondary)] inline-flex items-center gap-1.5"
            title="Reset all providers to Test Lab defaults"
          >
            <HiRefresh className="w-4 h-4" />
            Test Lab Defaults
          </button>
          <button
            type="button"
            onClick={() => lab.setSettingsOpen(true)}
            aria-label="Open test settings"
            className="px-3 py-1.5 text-xs font-medium bg-[var(--muted)] text-[var(--foreground)] rounded-md hover:bg-[var(--secondary)] inline-flex items-center gap-1.5"
          >
            <HiCog className="w-4 h-4" />
            Settings
          </button>
        </div>
      </div>
    </div>
  );
};

interface StatProps {
  label: string;
  value: string;
  accent: "up" | "down" | "neutral";
  deltaLabel?: string;
}

const Stat: React.FC<StatProps> = ({ label, value, accent, deltaLabel }) => (
  <div className="flex items-center gap-2 text-xs">
    <span className="text-[var(--foreground-muted)] uppercase tracking-wide">
      {label}
    </span>
    <span className="text-sm font-semibold text-[var(--foreground)] tabular-nums">
      {value}
    </span>
    {deltaLabel && (
      <span
        className={`inline-flex items-center gap-0.5 tabular-nums ${
          accent === "up"
            ? "text-green-600"
            : accent === "down"
              ? "text-red-600"
              : "text-[var(--foreground-muted)]"
        }`}
      >
        {accent === "up" ? (
          <HiArrowSmUp className="w-3.5 h-3.5" />
        ) : accent === "down" ? (
          <HiArrowSmDown className="w-3.5 h-3.5" />
        ) : null}
        {deltaLabel}
      </span>
    )}
  </div>
);
